import { useState } from 'react';
import { Bot, Check, Copy, MessageSquareWarning, Rocket } from 'lucide-react';
import { Link, useOutletContext, useParams } from 'react-router';
import { PageContainer } from '@/components/page-container';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { WhatsAppGlyph, InstagramGlyph, FacebookGlyph } from '@/components/platform-glyph';
import { useFacebookEnabled, useInstagramEnabled } from '@/hooks/use-app-config';
import { useConnectMetaSignup } from '@/components/use-connect-meta-signup';
import { useConnectInstagramLogin } from '@/components/use-connect-instagram-login';
import { useWorkspace } from '@/contexts/workspace-context';
import { useFetch } from '@/hooks/use-fetch';
import { workspaceUrl } from '@/lib/workspace-url';
import { BootstrapCodeDialog } from '@/pages/sandbox/bootstrap-code-dialog';
import { useLaunchpad } from './launchpad/use-launchpad';
import { useAlertPhone } from './launchpad/use-alert-phone';
import { AlertPhoneDialog } from '@/components/alert-phone-dialog';
import { RemoveAlertPhoneButton } from '@/components/remove-alert-phone-button';

/**
 * One launchpad row — brand icon (left), title + subtitle (middle), action
 * (right). Modelled on the GHL launchpad: a single card of divider-separated
 * rows rather than separate cards. When a step is done its left icon tile flips
 * to a green check so the glyph itself is the completion signal.
 */
function StepRow({
  icon,
  iconClass,
  title,
  subtitle,
  done = false,
  action,
  doneAction,
}: {
  icon: React.ReactNode;
  /** Icon-tile tint when NOT done (done always shows the green check tile). */
  iconClass?: string;
  title: string;
  subtitle: React.ReactNode;
  done?: boolean;
  action?: React.ReactNode;
  /** Rendered INSTEAD of `action` once done — for steps that stay manageable. */
  doneAction?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-5 py-4 sm:flex-row sm:items-center sm:gap-4">
      <div
        className={`flex size-11 shrink-0 items-center justify-center rounded-xl ${
          done ? 'bg-green-500/10' : (iconClass ?? 'bg-muted')
        }`}
        aria-hidden="true"
      >
        {done ? <Check className="size-5 text-green-500" /> : icon}
      </div>
      <div className="min-w-0 text-center sm:flex-1 sm:text-left">
        <p className="font-medium leading-tight">{title}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
      </div>
      <div className="flex shrink-0 items-center sm:w-44 sm:justify-end">
        {done ? doneAction : action}
      </div>
    </div>
  );
}

export function LaunchpadPage() {
  const { ws } = useParams<{ ws: string }>();
  const { activeWorkspace } = useWorkspace();
  const fetchWithAuth = useFetch();
  const { status, loading, error, markSkillsDone } = useLaunchpad();
  // AIT-370 — private alert phone (user-scoped, not workspace-scoped).
  const alertPhone = useAlertPhone();
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);

  const wsPublicId = activeWorkspace?.id;
  const instagramEnabled = useInstagramEnabled();
  const facebookEnabled = useFacebookEnabled();
  // The Facebook dialog and Page picker live in the Connect Channel modal.
  const { onConnectClick } = useOutletContext<{ onConnectClick: () => void }>();
  const handleWhatsAppClick = useConnectMetaSignup(wsPublicId, fetchWithAuth);
  const handleInstagramClick = useConnectInstagramLogin(wsPublicId, fetchWithAuth);

  if (loading) {
    return (
      <PageContainer variant="scroll">
        <Skeleton className="mb-8 h-8 w-72" />
        <Skeleton className="h-[22rem] w-full rounded-xl" />
      </PageContainer>
    );
  }

  const s = status ?? { whatsapp: false, instagram: false, facebook: false, cli: false, skills: false, allComplete: false };

  return (
    <PageContainer variant="scroll">
      <header className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">
          Let&apos;s get you on the path to success
        </h1>
      </header>
      {error && (
        <p className="mb-4 text-sm text-destructive" role="alert">
          Could not load your progress. Steps shown as incomplete.
        </p>
      )}

      <Card className="gap-0 divide-y divide-border overflow-hidden p-0">
        {/* AI-agent setup first — same minted dashboard instruction block as
            the dashboard card / header bell (CLI + login + skills + MCP). */}
        <StepRow
          icon={<Bot className="size-5 text-muted-foreground" />}
          title="Set up your AI agent"
          subtitle={
            <>
              Copy this and paste it into Claude Code, Cursor, or any AI agent.
              <br />
              It sets everything up so your agent can build your app and send messages for you.
            </>
          }
          done={s.cli && s.skills}
          action={
            <div className="flex items-center gap-2">
              <BootstrapCodeDialog
                variant="dashboard"
                trigger={
                  <Button variant="outline" size="sm">
                    <Copy className="size-3.5" />
                    Copy
                  </Button>
                }
              />
              <Button variant="default" size="sm" onClick={markSkillsDone}>
                Mark Done
              </Button>
            </div>
          }
        />

        <StepRow
          icon={<WhatsAppGlyph className="size-5" />}
          iconClass="bg-[#25D366]/10"
          title="Connect WhatsApp"
          subtitle={
            <>
              Add a new phone number or connect your existing WhatsApp Business app.
              <br />
              Start messaging your customers now.
            </>
          }
          done={s.whatsapp}
          action={
            <Button
              variant="default"
              size="sm"
              disabled={!wsPublicId}
              onClick={() => void handleWhatsAppClick()}
            >
              Connect
            </Button>
          }
        />

        <StepRow
          icon={<InstagramGlyph className="size-5" />}
          iconClass="bg-gradient-to-br from-[#FA7E1E]/15 via-[#833AB4]/15 to-[#5851DB]/15"
          title="Connect Instagram"
          subtitle={
            <>
              Connect your Instagram Business or Creator account.
              <br />
              {instagramEnabled
                ? 'Start sending DMs to your followers now.'
                : 'Instagram connections are coming back soon.'}
            </>
          }
          done={s.instagram}
          action={
            <Button
              variant="default"
              size="sm"
              disabled={!instagramEnabled || !wsPublicId}
              title={
                instagramEnabled
                  ? undefined
                  : 'Instagram connect is temporarily unavailable'
              }
              onClick={() => void handleInstagramClick()}
            >
              {instagramEnabled ? 'Connect' : 'Coming Soon'}
            </Button>
          }
        />

        {facebookEnabled && (
          <StepRow
            icon={<FacebookGlyph className="size-5" />}
            iconClass="bg-[#0866FF]/10"
            title="Connect a Facebook Page"
            subtitle={
              <>
                Messenger conversations, Page posts and comments.
                <br />
                A linked Instagram account connects with it.
              </>
            }
            done={s.facebook}
            action={
              <Button variant="default" size="sm" disabled={!wsPublicId} onClick={onConnectClick}>
                Connect
              </Button>
            }
          />
        )}

        <StepRow
          icon={<MessageSquareWarning className="size-5 text-amber-500" />}
          iconClass="bg-amber-500/10"
          title="Get alerts if something breaks"
          subtitle={
            alertPhone.status?.verified
              ? `Alerts go to ${alertPhone.status.phone}.`
              : 'If something ever stops working we will let you know!'
          }
          done={alertPhone.status?.verified === true}
          action={
            <Button variant="default" size="sm" onClick={() => setPhoneDialogOpen(true)}>
              Add phone
            </Button>
          }
          doneAction={
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => setPhoneDialogOpen(true)}>
                Change
              </Button>
              <RemoveAlertPhoneButton onRemoved={() => void alertPhone.refresh()} />
            </div>
          }
        />

        <StepRow
          icon={<Rocket className="size-5 text-primary" />}
          iconClass="bg-primary/10"
          title="Quick Start"
          subtitle={
            <>
              New here? Download a ready-made project and send your first message in minutes.
              <br />
              The fastest way to see how it all works.
            </>
          }
          action={
            ws && (
              <Link to={workspaceUrl(ws, 'sandbox/quick-start')}>
                <Button variant="default" size="sm">
                  Open
                </Button>
              </Link>
            )
          }
        />
      </Card>
      <AlertPhoneDialog
        open={phoneDialogOpen}
        onOpenChange={setPhoneDialogOpen}
        onVerified={() => void alertPhone.refresh()}
        current={alertPhone.status}
      />
    </PageContainer>
  );
}
